// components/features/tasks/TasksPage.tsx
declare global {
  interface Window {
    __skipNextFetch?: boolean;
  }
}

import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { BaseTable, BaseColumn } from "@/components/common/table/BaseTable";
import { TaskFilters } from "./TaskFilters";
import { useTaskStore } from "@/store";
import { Dropdown } from "@/components/common/Dropdown";
import { Button } from "@/components/common/Button";
import {
  Calendar,
  Download,
  Plus,
  Upload,
  Columns,
  Trash2,
  RefreshCw,
  KeyboardIcon,
} from "lucide-react";
import { AddTaskModal } from "@/components/modals/AddTaskModal";
import { AddClientModal } from "@/components/modals/AddClientModal";
import { EndDayModal } from "@/components/modals/EndDayModal";
import { CommentModal } from "@/components/modals/CommentModal";
import { EstimatedTimeModal } from "@/components/modals/EstimatedTimeModal";
import { CreateQuotationModal } from "@/components/modals/CreateQuotationModal";
import { DeleteTaskModal } from "@/components/modals/DeleteTaskModal";
import { Task } from "@/types";
import {
  TASK_STATUS,
  TASK_PRIORITIES,
  FREQUENCY_OPTIONS,
} from "@/utils/constants";
import { useTaskDeletion } from "./hooks/useTaskDeletion";
import { useTaskAutoSave } from "./hooks/useTaskAutoSave";
import { useTaskColumns } from "./hooks/useTaskColumns";
import { useTaskModals } from "./hooks/useTaskModals";
import { useTaskTabs } from "./hooks/useTaskTabs";
import { useTaskData } from "./hooks/useTaskData";
import { useTaskOperations } from "./hooks/useTaskOperations";
import { useNewTasks } from "./hooks/useNewTasks";
import { ExcelTableWrapper } from "@/components/common/table/ExcelTableWrapper";
import { useExcelEditing } from "@/hooks/useExcelEditing";
import { BaseTableCell } from "@/components/common/table/BaseTableCell";
import { useTableColumns } from "@/hooks/useTableColumns";
import CreateStatusModal from "@/components/modals/CreateStatusModal";

// Keyboard Shortcuts Integration
import { setTaskPageFunctions, shortcutManager } from "@/components/keyboard";
import toast from "react-hot-toast";
import Tooltip from "@/components/common/Tooltip";

export const TasksPage: React.FC = () => {
  // ============================================================================
  // STATE & REFS
  // ============================================================================
  const {
    tasks,
    loading: tasksLoading,
    fetchTasks,
    filters,
    setFilters,
    filterCounts,
    selectedTasks,
    toggleTaskSelection,
    toggleAllTasks,
    createTask,
    updateTask,
    deleteTask,
  } = useTaskStore();

  const tempIdToRealIdMap = useRef<Map<string, string>>(new Map());
  const creatingTasksRef = useRef<Set<string>>(new Set());
  const lastFilterUpdateRef = useRef<{ [key: string]: { time: number; value: string[] } }>({});

  const { columns, handleColumnChange } = useTableColumns("tasks");
  const {
    showAddModal,
    setShowAddModal,
    showAddClientModal,
    setShowAddClientModal,
    showEndDayModal,
    setShowEndDayModal,
    showQuotationPanel,
    setShowQuotationPanel,
    modal,
    openModal,
    closeModal,
  } = useTaskModals();
  const { activeTab, setActiveTab, tabCounts, mainTabs } = useTaskTabs();
  const {
    clients,
    clientsLoading,
    users,
    usersLoading,
    searchTerm,
    setSearchTerm,
  } = useTaskData();
  const { editingCell, setEditingCell, handleExistingTaskChange } =
    useTaskOperations(updateTask);
  const { newTaskRows, setNewTaskRows, addNewTaskRow, handleNewTaskChange } =
    useNewTasks();
  const { handleNewTaskBlur } = useTaskAutoSave({
    newTaskRows,
    createTask,
    fetchTasks,
    filters,
    setNewTaskRows,
  });
  const { cancelSelectedTasks, selectedNewTaskCount, showDeleteNewTasks } =
    useTaskDeletion({
      selectedTasks,
      newTaskRows,
      setNewTaskRows,
      toggleTaskSelection,
    });
  const [showCreateStatusModal, setShowCreateStatusModal] = useState(false);

  const handleColumnVisibilityChange = useCallback(
    (selectedColumnKeys: string[]) => {
      console.log("Column visibility change requested:", selectedColumnKeys);

      // Create a new columns array with updated visibility
      const updatedColumns = columns.map((column) => ({
        ...column,
        visible: selectedColumnKeys.includes(column.key),
      }));

      // Call the original handleColumnChange with the updated columns
      // If handleColumnChange expects different parameters, adjust accordingly
      handleColumnChange(selectedColumnKeys);

      // Force a re-render by updating local state if needed
      // You might need to add a local state to force updates
    },
    [columns, handleColumnChange]
  );
  const handleManualRefresh = useCallback(() => {
    tempIdToRealIdMap.current.clear();
    setNewTaskRows([]);
    fetchTasks(filters);
  }, [filters, fetchTasks, setNewTaskRows]);

  const handleTabClick = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      const tabFilter = mainTabs.find((t) => t.id === tabId)?.filter || {};
      const clearedFilters = {
        ...filters,
        isOverdue: undefined,
        dueToday: undefined,
        dueTomorrow: undefined,
        dueThisWeek: undefined,
        dueNext15Days: undefined,
        dueThisMonth: undefined,
        dueDateFrom: undefined,
        dueDateTo: undefined,
      };
      setFilters({ ...clearedFilters, ...tabFilter });
    },
    [mainTabs, filters, setFilters, setActiveTab]
  );

  const handleAddNewTaskRow = useCallback(() => {
    if (newTaskRows.length < 5) {
      addNewTaskRow();
    }
  }, [newTaskRows.length, addNewTaskRow]);

  const handleExportTasks = useCallback(() => {
    try {
      const csvContent = tasks
        .map(
          (task) =>
            `${task.task || ""},${task.subTask || ""},${task.status || ""},${
              task.priority || ""
            },${task.et || 0},${task.tt || 0}`
        )
        .join("\n");

      const blob = new Blob(
        [`Task,SubTask,Status,Priority,ET,TT\n${csvContent}`],
        { type: "text/csv" }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tasks-${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success("Tasks exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export tasks");
    }
  }, [tasks]);

  const handleImportTasks = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        toast.success(`Importing ${file.name}...`);
        // Add your CSV import logic here
        const reader = new FileReader();
        reader.onload = (event) => {
          const csvData = event.target?.result as string;
          console.log("CSV Data:", csvData);
          // Parse and import CSV data
          // You can add your CSV parsing logic here
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, []);

  const handleSaveChanges = useCallback(() => {
    console.log("🎯 Saving all changes...");
    // Auto-save logic is already handled by useTaskAutoSave hook
    toast.success("All changes saved!");
  }, []);

  const handleDeleteSelectedTasks = useCallback(() => {
    if (showDeleteNewTasks) {
      cancelSelectedTasks();
      toast.success("Selected new tasks deleted");
    } else {
      const selectedCount = Object.values(selectedTasks).filter(Boolean).length;
      if (selectedCount > 0) {
        const selectedTaskIds = Object.entries(selectedTasks)
          .filter(([_, selected]) => selected)
          .map(([id]) => id);

        // Delete each selected task
        selectedTaskIds.forEach((id) => {
          deleteTask(id);
        });

        toast.success(
          `Deleted ${selectedCount} selected task${
            selectedCount > 1 ? "s" : ""
          }`
        );
      } else {
        toast.info("No tasks selected to delete");
      }
    }
  }, [showDeleteNewTasks, cancelSelectedTasks, selectedTasks, deleteTask]);

  const handleEndDay = useCallback((date: Date) => {
    console.log("End day selected for date:", date);
    // Add your end day logic here
  }, []);

  // Move dropdownConfig BEFORE handleFilterChange
  const dropdownConfig = useMemo(
    () => ({
      client: {
        items: clients.map((client) => ({
          id: client._id || client.id,
          label: client.name,
        })),
        showSearch: true,
        showSearchBar: true,
        showAdd: true,
        addLabel: "Add",
        onAdd: () => setShowAddClientModal(true),
        loading: clientsLoading,
      },
      allottedTo: {
        items: users.map((user) => ({
          id: user._id || user.id,
          label: user.name,
        })),
        showSearchBar: true,
        showSearch: true,
        loading: usersLoading,
      },
      teamLeader: {
        items: users.map((user) => ({
          id: user._id || user.id,
          label: user.name,
        })),
        showSearchBar: true,
        showSearch: true,
        loading: usersLoading,
      },
      priority: {
        items: [
          { id: TASK_PRIORITIES.HIGH, label: "High" },
          { id: TASK_PRIORITIES.MEDIUM, label: "Medium" },
          { id: TASK_PRIORITIES.LOW, label: "Low" },
        ],
      },
      status: {
        items: [
          { id: TASK_STATUS.TODO, label: "To Do", color: "#EF4444" },
          { id: TASK_STATUS.IN_PROCESS, label: "In Process", color: "#F59E0B" },
          { id: TASK_STATUS.IN_REVIEW, label: "In Review", color: "#10B981" },
          { id: TASK_STATUS.COMPLETED, label: "Completed", color: "#3B82F6"},
                    { id: TASK_STATUS.ARCHIVED, label: "Archived", color: "#8B5CF6" },
          {
            id: TASK_STATUS.HELP_REQUIRED,
            label: "Help Required",
            color: "#F97316",
          },
        ],
        showAdd: true,
        addLabel: "Add Status",
        onAdd: () => setShowCreateStatusModal(true),
      },
      frequency: {
        items: [
          { id: FREQUENCY_OPTIONS.NONE, label: "None" },
          { id: FREQUENCY_OPTIONS.MONTHLY, label: "Monthly" },
          { id: FREQUENCY_OPTIONS.QUARTERLY, label: "Quarterly" },
          { id: FREQUENCY_OPTIONS.YEARLY, label: "Yearly" },
        ],
      },
      task: {
        items: Array.from(
          new Set(tasks.map((task) => task.task).filter(Boolean))
        ).map((taskName) => ({
          id: taskName,
          label: taskName,
        })),
        showSearch: true,
      },
      subTask: {
        items: Array.from(
          new Set(tasks.map((task) => task.subTask).filter(Boolean))
        ).map((subTaskName) => ({
          id: subTaskName,
          label: subTaskName,
        })),
        showSearch: true,
      },
    }),
    [clients, clientsLoading, users, usersLoading, tasks, setShowAddClientModal, setShowCreateStatusModal]
  );

const handleFilterChange = useCallback(
  (columnKey: string, ids: string[]) => {
    console.log("🎯 handleFilterChange called:", columnKey, ids);
    
    const dropdownItems = dropdownConfig[columnKey]?.items || [];
    
    // For the API approach: empty array means "all selected" (no filter needed)
    if (ids.length === 0 || (ids.length === dropdownItems.length && dropdownItems.length > 0)) {
      // All items selected or empty array = remove filter to get all items
      console.log(`📤 All ${columnKey} items selected - removing filter`);
      
      const update = { ...filters };
      delete update[columnKey];
      setFilters(update);
    } else {
      // Partial selection = apply filter with selected IDs
      console.log(`📥 Partial ${columnKey} selection - applying filter with ${ids.length} items`);
      
      const update = { ...filters };
      update[columnKey] = ids;
      setFilters(update);
    }
  },
  [filters, setFilters, dropdownConfig]
);

  useEffect(() => {
    setTaskPageFunctions({
      addNewTaskRow: () => {
        console.log("⌨️ Keyboard: Add new task");
        if (newTaskRows.length < 5) {
          handleAddNewTaskRow();
          toast.success("New task row added");
        } else {
          toast.error("Maximum 5 new tasks allowed");
        }
      },
      refreshTasks: () => {
        console.log("⌨️ Keyboard: Refresh tasks");
        handleManualRefresh();
        toast.success("Tasks refreshed");
      },
      selectAllTasks: () => {
        console.log("⌨️ Keyboard: Select all tasks");
        toggleAllTasks();
        const allSelected = Object.values(selectedTasks).every(Boolean);
        toast.success(
          allSelected ? "All tasks deselected" : "All tasks selected"
        );
      },
      deleteSelectedTasks: () => {
        console.log("⌨️ Keyboard: Delete selected tasks");
        handleDeleteSelectedTasks();
      },
      exportTasks: () => {
        console.log("⌨️ Keyboard: Export tasks");
        handleExportTasks();
      },
      importTasks: () => {
        console.log("⌨️ Keyboard: Import tasks");
        handleImportTasks();
      },
      saveChanges: () => {
        console.log("⌨️ Keyboard: Save changes");
        handleSaveChanges();
      },
      endDay: () => {
        console.log("⌨️ Keyboard: End day");
        setShowEndDayModal(true);
        toast.success("End day modal opened");
      },
      addClient: () => {
        console.log("⌨️ Keyboard: Add client");
        setShowAddClientModal(true);
        toast.success("Add client modal opened");
      },
      addStatus: () => {
        console.log("⌨️ Keyboard: Add status");
        setShowCreateStatusModal(true);
        toast.success("Add status modal opened");
      },
      switchToTab: (tabIndex: number) => {
        console.log("⌨️ Keyboard: Switch to tab", tabIndex);
        const tab = mainTabs[tabIndex];
        if (tab) {
          handleTabClick(tab.id);
          toast.success(`Switched to ${tab.label}`);
        }
      },
    });

    return () => {
      setTaskPageFunctions({});
    };
  }, [
    newTaskRows.length,
    handleAddNewTaskRow,
    handleManualRefresh,
    toggleAllTasks,
    selectedTasks,
    handleDeleteSelectedTasks,
    handleExportTasks,
    handleImportTasks,
    handleSaveChanges,
    setShowEndDayModal,
    setShowAddClientModal,
    setShowCreateStatusModal,
    mainTabs,
    handleTabClick,
  ]);

  const enhancedHandleNewTaskChange = useCallback(
    async (tempId: string, key: keyof Task, value: any) => {
      handleNewTaskChange(tempId, key, value);

      const realId = tempIdToRealIdMap.current.get(tempId);

      if (realId) {
        await updateTask(realId, { [key]: value });
        return;
      }

      if (
        !value ||
        value === "" ||
        (typeof value === "number" && value === 0)
      ) {
        return;
      }

      const task = newTaskRows.find((row) => row.tempId === tempId);

      if (!task) {
        return;
      }

      if (task._id) {
        await updateTask(task._id, { [key]: value });
        return;
      }

      if (creatingTasksRef.current.has(tempId)) {
        return;
      }

      creatingTasksRef.current.add(tempId);

      try {
        const taskData = {
          [key]: value,
          priority: TASK_PRIORITIES.MEDIUM,
          status: TASK_STATUS.TODO,
          frequency: FREQUENCY_OPTIONS.NONE,
          et: 0,
          tt: 0,
        };

        const createdTask = await createTask(taskData);

        if (createdTask && createdTask._id) {
          tempIdToRealIdMap.current.set(tempId, createdTask._id);
          setNewTaskRows((prev) => {
            const filtered = prev.filter((row) => row.tempId !== tempId);
            return filtered;
          });
        } else {
          console.log("🟥 No _id in created task response!");
        }
      } catch (error) {
        console.error("🟥 Error creating task:", error);
      } finally {
        setTimeout(() => {
          creatingTasksRef.current.delete(tempId);
        }, 1000);
      }
    },
    [handleNewTaskChange, updateTask, newTaskRows, createTask, setNewTaskRows]
  );
  
  const displayTasks = useMemo(() => {
    const createdRealIds = new Set<string>();
    newTaskRows.forEach((row) => {
      if (row._id) {
        createdRealIds.add(row._id);
      }
    });

    const filteredTasks = tasks.filter((task) => {
      const shouldExclude = task._id && createdRealIds.has(task._id);
      return !shouldExclude;
    });

    return [...newTaskRows, ...filteredTasks];
  }, [newTaskRows, tasks]);

  const visibleColumnsCount = columns.filter((c) => c.visible).length;

  const totalEt = useMemo(
    () => tasks.reduce((sum, t) => sum + (t.et || 0), 0),
    [tasks]
  );

  const totalTt = useMemo(
    () => tasks.reduce((sum, t) => sum + (t.tt || 0), 0),
    [tasks]
  );

  const totalMinutes = totalEt + totalTt;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const selectedCount = useMemo(() => {
    return Object.values(selectedTasks).filter(Boolean).length;
  }, [selectedTasks]);

  const {
    currentCell: excelCurrentCell,
    isEditing: isExcelEditing,
    startEditing: startExcelEditing,
    stopEditing: stopExcelEditing,
    moveToNextCell,
  } = useExcelEditing({
    totalRows: displayTasks.length,
    totalCols: visibleColumnsCount,
    onCellEdit: (rowIndex, colIndex) => {
      const task = displayTasks[rowIndex];
      const column = columns.filter((c) => c.visible)[colIndex];
      if (task && column && !["serial", "actions"].includes(column.key)) {
        const taskId = task.tempId || task._id || task.id || "";
        setEditingCell({ taskId, columnKey: column.key });
      }
    },
    onCellSave: (rowIndex, colIndex) => {
      setEditingCell(null);
    },
  });

  const tableColumns: BaseColumn[] = useMemo(() => {
    const visibleColumns = columns.filter((col) => col.visible);

    return visibleColumns.map((col) => ({
      ...col,
      editable: !["serial", "actions"].includes(col.key),
      render: (task: Task, index: number) => {
        const isNewTask = !!(task as any).tempId;
        const hasRealId = !!(task._id || task.id);
        const taskId = task.tempId || task._id || task.id || "";
        const isEditing =
          editingCell?.taskId === taskId && editingCell?.columnKey === col.key;
        const newTaskCount = newTaskRows.length;
        const serialNumber = isNewTask
          ? 0
          : tasks.length - (index - newTaskCount);

        return (
          <BaseTableCell
            key={`${taskId}-${col.key}`}
            task={task}
            column={col}
            index={index}
            isNewTask={isNewTask}
            hasRealId={hasRealId}
            isEditing={isEditing}
            onEditStart={() => {
              const cellTaskId =
                isNewTask && task.tempId
                  ? task.tempId
                  : task._id || task.id || "";
              setEditingCell({ taskId: cellTaskId, columnKey: col.key });
            }}
            onEditEnd={() => setEditingCell(null)}
            onNewTaskChange={
              isNewTask ? enhancedHandleNewTaskChange : undefined
            }
            onExistingTaskChange={
              hasRealId && !isNewTask ? handleExistingTaskChange : undefined
            }
            clientsList={clients}
            users={users}
            openModal={openModal}
            totalTasks={displayTasks.length}
            serialStart={serialNumber}
            dropdownConfig={dropdownConfig}
          />
        );
      },
    }));
  }, [
    columns,
    displayTasks.length,
    editingCell,
    enhancedHandleNewTaskChange,
    handleExistingTaskChange,
    clients,
    users,
    openModal,
    newTaskRows.length,
    tasks.length,
    dropdownConfig,
  ]);

  const selectedFilters = useMemo(() => {
    console.log("📊 Current filters for selectedFilters:", filters);
    
    const ensureArray = (value: any): string[] => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string' && value) return [value];
      return [];
    };
    
    return {
      client: ensureArray(filters.client),
      allottedTo: ensureArray(filters.allottedTo),
      teamLeader: ensureArray(filters.teamLeader),
      priority: ensureArray(filters.priority),
      status: ensureArray(filters.status),
      frequency: ensureArray(filters.frequency),
      task: ensureArray(filters.task),
      subTask: ensureArray(filters.subTask),
    };
  }, [filters]);

  useEffect(() => {
    fetchTasks(filters);
  }, []);

  useEffect(() => {
    if (window.__skipNextFetch) {
      window.__skipNextFetch = false;
      return;
    }
    if (newTaskRows.length > 0) {
      return;
    }

    const filterParams = { ...filters };

    if (searchTerm && searchTerm.length > 1) {
      filterParams.search = searchTerm.trim();
    } else if (searchTerm === "") {
      filterParams.search = undefined;
    }

    fetchTasks(filterParams);
  }, [
    filters.status,
    filters.priority,
    filters.isOverdue,
    filters.dueToday,
    filters.dueTomorrow,
    filters.dueThisWeek,
    filters.dueNext15Days,
    filters.dueThisMonth,
    searchTerm,
    filters,
    newTaskRows.length,
    fetchTasks,
  ]);

  useEffect(() => {
    return () => {
      tempIdToRealIdMap.current.clear();
      creatingTasksRef.current.clear();
    };
  }, []);

  const handleDirectDelete = useCallback(async (taskId: string) => {
    try {
      const taskToDelete = tasks.find(task => task._id === taskId);
      
      if (!taskToDelete) {
        toast.error("Task not found");
        return;
      }

      // Check if task can be deleted (only archived tasks)
      if (taskToDelete.status !== TASK_STATUS.ARCHIVED) {
        toast.error("Only archived tasks can be deleted");
        return;
      }

      // Confirm deletion
      const confirmed = window.confirm("Are you sure you want to delete this archived task?");
      
      if (confirmed) {
        await deleteTask(taskId);
        toast.success("Task deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  }, [tasks, deleteTask]);

  return (
    <div className="h-full bg-[#FFFFFF] flex flex-col relative">
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        {/* Tab Navigation */}
        <div className="flex items-center px-3 sm:px-3 overflow-x-auto border-b">
          <div className="flex space-x-1 mt-2">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`flex items-center flex-shrink-0 px-1 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "text-blue-600 border-blue-600"
                    : "text-gray-500 border-transparent hover:text-gray-700"
                }`}
              >
                <span className="truncate">{tab.label}</span>
                <span className="ml-1 text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                  {tabCounts[tab.id] ?? 0}
                </span>
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="ml-auto hidden sm:flex items-center space-x-2">
            {/* Manage Columns */}
            <Dropdown
              button={
                <Button
                  variant="secondary"
                  size="xs"
                  className="border border-[#E5E5E5] text-[#171717]"
                  style={{border:"2px solid #E5E5E5",color:"#171717"}}
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.4001 2.40002H10.8001C10.1374 2.40002 9.6001 2.93728 9.6001 3.60002V12.4C9.6001 13.0628 10.1374 13.6 10.8001 13.6H12.4001C13.0628 13.6 13.6001 13.0628 13.6001 12.4V3.60002C13.6001 2.93728 13.0628 2.40002 12.4001 2.40002Z"
                        stroke="#737373"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M5.1999 2.40002H3.5999C2.93716 2.40002 2.3999 2.93728 2.3999 3.60002V12.4C2.3999 13.0628 2.93716 13.6 3.5999 13.6H5.1999C5.86264 13.6 6.3999 13.0628 6.3999 12.4V3.60002C6.3999 2.93728 5.86264 2.40002 5.1999 2.40002Z"
                        fill="#737373"
                        stroke="#737373"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  }
                  title="Manage columns"
                >
                  Manage Columns
                </Button>
              }
              items={columns.map((c) => ({ id: c.key, label: c.label }))}
              selected={columns.filter((c) => c.visible).map((c) => c.key)}
              onChange={handleColumnVisibilityChange}
              withCheckboxes
              withSelectAll
            />

            {/* Export Button */}
            <Button
              variant="secondary"
              size="xs"              
              icon={<Download size={16} />}
              className="hidden sm:flex border border-[#E5E5E5] text-[#171717]"
              style={{border:"2px solid #E5E5E5",color:"#171717"}}
              title="Export data (Cmd/Ctrl+Alt+Shift+E)"
              onClick={handleExportTasks}
            >
              Export
            </Button>

            {/* End Day Button */}
            <Button
              variant="danger"
              size="xs"
              icon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.5999 6.39995V5.59995C13.5999 4.27443 12.5254 3.19995 11.1999 3.19995H4.7999C3.47438 3.19995 2.3999 4.27443 2.3999 5.59995V6.39995H13.5999Z"
                    fill="#F43F5E"
                  />
                  <path
                    d="M4.7998 3.19998V1.59998"
                    stroke="#F43F5E"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M11.2 3.19998V1.59998"
                    stroke="#F43F5E"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M13.5999 5.59995C13.5999 4.27443 12.5254 3.19995 11.1999 3.19995H4.7999C3.47438 3.19995 2.3999 4.27443 2.3999 5.59995V10.4C2.3999 11.7255 3.47438 12.8 4.7999 12.8H5.48406"
                    stroke="#F43F5E"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 11.6L9.8 13.6L13.6 8.79999"
                    stroke="#F43F5E"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              }
              className="whitespace-nowrap"
              onClick={() => setShowEndDayModal(true)}
              title="End day (Cmd/Ctrl+Alt+D)"
            >
              End Day
            </Button>

            {/* Divider */}
            <div className="border-r-[2] border-gray-700 h-5 w-[2px]"></div>

            {/* Import Button */}
            <Button
              variant="secondary"
              size="xs"
              icon={<Upload size={16} />}
              className="hidden sm:flex border-[#2B80FF] text-[#2B80FF]"
              title="Import CSV (Cmd/Ctrl+Alt+Shift+I)"
              onClick={handleImportTasks}
            >
              Import
            </Button>

            {/* Delete New Tasks Button (Conditional) */}
            {showDeleteNewTasks && (
              <Button
                variant="danger"
                size="xs"
                icon={<Trash2 size={16} />}
                onClick={cancelSelectedTasks}
                title="Delete selected new tasks (Delete)"
              >
                Delete New ({selectedNewTaskCount})
              </Button>
            )}

            {/* Refresh Button */}
            {/* <Button
              variant="secondary"
              size="xs"
              icon={<RefreshCw size={16} />}
              onClick={handleManualRefresh}
              className="hidden sm:flex"
              title="Refresh tasks (Cmd/Ctrl+Alt+R)"
            >
              Refresh
            </Button> */}

            {/* Add Task Button */}
            <Button
              variant="primary"
              size="xs"
              icon={<Plus size={16} />}
              onClick={handleAddNewTaskRow}
              disabled={newTaskRows.length >= 5}
              className="hidden sm:flex"
              title="Add new task (Cmd/Ctrl+Alt+N)"
            >
              <span className="hidden sm:inline">
                Add Task {newTaskRows.length > 0 && `(${newTaskRows.length}/5)`}
              </span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="px-3 sm:px-3 py-3 overflow-x-auto">
          <TaskFilters
            filters={filters}
            setFilters={setFilters}
            filterCounts={filterCounts}
            fetchTasks={fetchTasks}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full px-3 sm:px-3">
          <div className="h-full -mx-3 sm:-mx-3 overflow-x-auto overflow-y-auto ">
            <ExcelTableWrapper
              data={displayTasks}
              columns={tableColumns}
              loading={tasksLoading}
              selectable={true}
              selectedItems={selectedTasks}
                           onToggleSelection={toggleTaskSelection}
              onToggleAllSelection={toggleAllTasks}
              getItemId={(task) =>
                task.tempId || task._id || task.id || "new-row"
              }
              showFilters={true}
              dropdownConfig={dropdownConfig}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              sortable={false}
              onRowClick={(task) => {
                if (!(task as any).tempId && !editingCell) {
                  setShowQuotationPanel(true);
                }
              }}
              isNewRow={(task) => !!(task as any).tempId}
              tableClassName="task-table"
              emptyMessage="No tasks found"
              enableExcelNavigation={true}
              enableKeyboardShortcuts={true}
              onCellEnter={(rowIndex, colIndex, task) => {
                const column = columns.filter((c) => c.visible)[colIndex];
                if (column && !["serial", "actions"].includes(column.key)) {
                  const taskId = task.tempId || task._id || task.id || "";
                  setEditingCell({ taskId, columnKey: column.key });
                }
              }}
              onCellSelect={(rowIndex, colIndex, cell) => {
                console.log("Cell selected:", rowIndex, colIndex);
              }}
              isEditing={!!editingCell}
              onEditingChange={(editing) => {
                if (!editing) {
                  setEditingCell(null);
                }
              }}
            />
          </div>
        </div>
      </div>

      <table className="w-full border-t border-gray-200 bg-[#F5F5F5] text-black">
        <tbody>
          <tr className="text-xs">
            {/* Serial */}
            <td
              className="text-center py-2"
              style={{ width: "30px", minWidth: "30px" }}
            ></td>

            {/* Client - COUNT below this column */}
            <td
              className="text-end py-2 hover:bg-[#E4E4E4]"
              style={{ width: "125px", minWidth: "125px" }}
            >
              <Tooltip content={`Total tasks: ${tasks.length}`} position="top">
                <div className="flex items-center justify-between cursor-pointer w-full">
                  <span>COUNT</span>
                  <span className="font-semibold">{tasks.length}</span>
                </div>
              </Tooltip>
            </td>

            {/* Task */}
            <td
              className="text-center py-2"
              style={{ width: "125px", minWidth: "125px" }}
            ></td>

            {/* Sub-Task */}
            <td
              className="text-center py-2"
              style={{ width: "125px", minWidth: "125px" }}
            ></td>
            {/* ET - Estimated Time below this column */}
            <td
              className="text-end py-2 hover:bg-[#E4E4E4]"
              style={{ width: "60px", minWidth: "60px" }}
            >
              <Tooltip
                content={`${totalEt} mins (${Math.floor(totalEt / 60)}h ${
                  totalEt % 60
                }m)`}
                position="top"
              >
                <div className="flex w-full items-center justify-end space-x-1 cursor-pointer hover:bg-[#E4E4E4]">
                  <span className="font-semibold">{totalEt}</span>
                </div>
              </Tooltip>
            </td>
            {/* Allotted To */}
            <td
              className="text-center py-2"
              style={{ width: "125px", minWidth: "125px" }}
            ></td>
            {/* Team Leader */}
            <td
              className="text-center py-2"
              style={{ width: "125px", minWidth: "125px" }}
            ></td>

            {/* Priority */}
            <td
              className="text-center py-2"
              style={{ width: "90px", minWidth: "90px" }}
            ></td>

            {/* Due Date */}
            <td
              className="text-center py-2"
              style={{ width: "120px", minWidth: "120px" }}
            ></td>

            {/* Frequency */}
            <td
              className="text-center py-2"
              style={{ width: "125px", minWidth: "125px" }}
            ></td>

            {/* Status */}
            <td
              className="text-center py-2"
              style={{ width: "120px", minWidth: "120px" }}
            ></td>

            {/* TT - Tracked Time below this column */}
            <td
              className="text-center py-2 hover:bg-[#E4E4E4]"
              style={{ width: "80px", minWidth: "60px" }}
            >
              <Tooltip
                content={`${totalTt} mins (${Math.floor(totalTt / 60)}h ${
                  totalTt % 60
                }m)`}
                position="top"
              >
                <div className="flex items-center justify-end space-x-1 cursor-pointer">
                  <span className="font-semibold">{totalTt}</span>
                </div>
              </Tooltip>
            </td>

            {/* Actions */}
            <td
              className="text-center py-2"
              style={{ width: "120px", minWidth: "120px" }}
            ></td>
          </tr>
        </tbody>
      </table>

      <div className="sm:hidden fixed bottom-4 left-4 right-4 flex justify-between z-10">
        {showDeleteNewTasks ? (
          <Button
            variant="danger"
            size="sm"
            icon={<Trash2 size={16} />}
            onClick={cancelSelectedTasks}
            className="shadow-lg flex-1 mr-2"
            title="Delete selected new tasks (Delete)"
          >
            Delete ({selectedNewTaskCount})
          </Button>
        ) : null}

        <Button
          variant="secondary"
          size="sm"
          icon={<RefreshCw size={16} />}
          onClick={handleManualRefresh}
          className="shadow-lg flex-1 mr-2"
          title="Refresh tasks (Cmd/Ctrl+Alt+R)"
        >
          Refresh
        </Button>

        <Button
          variant="primary"
          size="sm"
          icon={<Plus size={16} />}
          onClick={handleAddNewTaskRow}
          disabled={newTaskRows.length >= 5}
          className="shadow-lg flex-1"
          title="Add new task (Cmd/Ctrl+Alt+N)"
        >
          Add ({newTaskRows.length}/5)
        </Button>
      </div>

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
      />

      {/* End Day Modal */}
      <EndDayModal
        isOpen={showEndDayModal}
        onClose={() => setShowEndDayModal(false)}
        onEndDay={handleEndDay}
      />

      {/* Create Status Modal */}
      <CreateStatusModal
        isOpen={showCreateStatusModal}
        onClose={() => setShowCreateStatusModal(false)}
        onStatusCreated={(newStatus) => {
          console.log("New status created:", newStatus);
          toast.success(`Status "${newStatus.name}" created successfully`);
        }}
      />

      {/* Comment Modal */}
      {modal.type === "comment" && modal.task && (
        <CommentModal
          isOpen={true}
          onClose={closeModal}
          taskId={modal.task.id!}
        />
      )}

      {/* Estimated Time Modal */}
      {modal.type === "time" && modal.task && (
        <EstimatedTimeModal
          isOpen={true}
          onClose={closeModal}
          taskId={modal.task.id!}
        />
      )}

      {/* Create Quotation Modal */}
      {modal.type === "quotation" && modal.task && (
        <CreateQuotationModal
          isOpen={true}
          onClose={closeModal}
          task={modal.task}
        />
      )}

      {/* Delete Task Modal */}
      {modal.type === "delete" && modal.task && (
        <DeleteTaskModal isOpen={true} onClose={closeModal} task={modal.task} />
      )}
    </div>
  );
};